import React from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import Svg from '../../Generic/Icons/index';
import { SortingParameters, ProductSorting } from '../../../redux/products/types';
import { StyledTextSpan, TextType } from '../../Generic/Text/StyledText';
import useTranslator from '../../../hooks/useTranslator';

interface Props {
    onSortValueChange: (newSortValue: SortingParameters) => void;
    onSortOpen: () => void;
    sorting: ProductSorting[];
    value: SortingParameters;
    className?: string;
}

const StyledFilterAndSortIcons = styled(Svg)`
    display: inline-block;
    width: 10px;
    height: 15px;
    color: ${({ theme }) => theme.colors.charcoal100};
    vertical-align: bottom;
    margin: 0 10px 4px 3px;
`;

const StyledSortText = styled(StyledTextSpan)`
    margin-bottom: 2px;
`;

export const StyledFilterAndSortButtons = styled.div`
    text-transform: capitalize;
    display: grid;

    grid-template-columns: 1fr;
    align-items: center;
`;

export const StyledSortDropdown = styled.select<{ textType: TextType }>`
    color: ${({ theme }) => theme.colors.charcoal100};
    opacity: 0;
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row: 1;
`;

const StyledIconAndTextWrapper = styled.div`
    grid-row: 1;
    grid-column: 1;
    ${forDesktop`
        text-align: right;
    `}
`;

const SortBox: React.SFC<Props> = ({ value, onSortOpen, onSortValueChange, sorting, className }) => {
    const sortingOptions: { [x: string]: SortingParameters } = sorting.reduce(
        (acc, val) => ({
            ...acc,
            [val.displayName]: { sort: val.name, order: val.order },
        }),
        {}
    );

    const t = useTranslator();

    const selectedOption =
        Object.keys(sortingOptions).find(
            key => sortingOptions[key].order === value.order && sortingOptions[key].sort === value.sort
        ) || Object.keys(sortingOptions)[0];

    const sendSortValueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSortValue: SortingParameters = sortingOptions[e.target.value];
        onSortValueChange(newSortValue);
    };

    return (
        <StyledFilterAndSortButtons className={className}>
            <StyledIconAndTextWrapper>
                <StyledFilterAndSortIcons type="productSortBy" />
                <StyledSortText textType="button">
                    {t('Sort')}: {selectedOption}
                </StyledSortText>
            </StyledIconAndTextWrapper>
            <StyledSortDropdown
                textType="button"
                value={selectedOption}
                onChange={sendSortValueChange}
                onClick={onSortOpen}
            >
                {Object.keys(sortingOptions).map(key => (
                    <option key={key} value={key}>
                        {t(key)}
                    </option>
                ))}
            </StyledSortDropdown>
        </StyledFilterAndSortButtons>
    );
};

export default SortBox;
